import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import type { TariffTypeFilters, TariffTypes } from '../models';
import * as tariffTypesService from '../services/tariff-types.service';

export const useTariffTypes = (
  defaultFilters: Partial<TariffTypeFilters & { searchParams: { ids: string[] } }> = {},
) => {
  const { searchParams, ...otherDefaultFilters } = useMemo(() => defaultFilters, [defaultFilters]);
  const [filters, setFilters] = useState<Partial<TariffTypeFilters>>(otherDefaultFilters);

  const cacheData = tariffTypesService.getAll();

  const updateFilters = useCallback((newFilters: Partial<TariffTypeFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const { data, isLoading, isError } = useQuery(tariffTypesService.tariffTypesQueryOptions(filters));

  const missingIds = useMemo(() => {
    if (!searchParams?.ids?.length || !data?.map) {
      return [];
    }
    return searchParams.ids.filter((id) => !data?.map[id] && !cacheData?.map[id]);
  }, [searchParams?.ids, data?.map, cacheData?.map]);

  const {
    data: preloadData,
    isLoading: isPreloadLoading,
    isError: isPreloadError,
  } = useQuery(
    tariffTypesService.tariffTypesQueryOptions(
      { search: { codeId: missingIds.join(',') } },
      { enabled: missingIds.length > 0 },
    ),
  );

  const mergedMap: TariffTypes['map'] = {
    ...(data?.map ?? {}),
    ...(cacheData?.map ?? {}),
    ...(preloadData?.map ?? {}),
  };

  return {
    data: {
      list: Object.values(mergedMap),
      map: mergedMap,
    },
    isError: isError || isPreloadError,
    isLoading: isLoading || isPreloadLoading,
    updateFilters,
  };
};
